"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Service = {
  number: string;
  title: string;
  descriptor: string;
  body: string;
  includes: string[];
  pricing: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
const services: Service[] = [{
  number: "01",
  title: "Pulmonary Care",
  descriptor: "Comprehensive diagnostic and therapeutic respiratory management.",
  body: "Led by Dr. Azmat Qayyum, our pulmonary practice focuses on the diagnostic evaluation and treatment of lung conditions. We utilize precise protocols to manage chronic respiratory diseases, ensuring each patient receives a tailored plan designed to improve overall quality of life.",
  includes: ["COPD and asthma care", "Shortness of breath checks", "Chronic cough evaluation", "Oxygen therapy support", "Follow-up after hospital stays"],
  pricing: "ADULT PULMONARY MEDICINE",
  image: "https://strong-hare.10web.cloud/wp-content/uploads/2026/06/ChatGPT-Image-Jun-4-2026-03_28_54-PM-5-3.png?v=eazqay989q",
  srcset: "https://strong-hare.10web.cloud/wp-content/uploads/2026/06/ChatGPT-Image-Jun-4-2026-03_28_53-PM-3.png?v=7c5eyrp1btk-wvc-srcset",
  sizes: "https://strong-hare.10web.cloud/wp-content/uploads/2026/06/ChatGPT-Image-Jun-4-2026-03_28_53-PM-3.png?v=fm65pdojl9e-wvc-sizes",
  alt: "Dr. Qayyum reviewing pulmonary diagnostic results with a patient in a clean clinical setting"
}, {
  number: "02",
  title: "Sleep Medicine",
  descriptor: "Evaluation and follow-up for sleep-related breathing concerns.",
  body: "Sleep is the foundation of systemic health. Our specialized sleep medicine services address management of sleep apnea and oxygen therapy. We provide thorough evaluations and continuous follow-up care to ensure your treatment remains effective and comfortable.",
  includes: ["Sleep apnea evaluation", "CPAP follow‑up", "BiPAP support", "Sleep-related breathing concerns", "Treatment follow‑up"],
  pricing: "SPECIALIZED SLEEP DISORDERS",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692402/img/sleep_medicine_care.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692402/img/sleep_medicine_care.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692402/img/sleep_medicine_care.png-wvc-sizes",
  alt: "Modern sleep medicine equipment and comfortable patient consultation room"
}, {
  number: "03",
  title: "Follow-Up & Ongoing Care",
  descriptor: "Continued care for chronic breathing and sleep-related concerns.",
  body: "Our office provides follow-up care for adults managing chronic lung conditions, sleep apnea, oxygen needs, post‑hospital respiratory issues, and related concerns. Treatment plans are reviewed over time based on symptoms, testing, and clinical needs.",
  includes: ["COPD and asthma follow-up", "Sleep apnea and CPAP concerns", "Oxygen therapy support", "Post‑hospital pulmonary follow‑up", "Chronic cough and shortness of breath"],
  pricing: "ONGOING CARE",
  image: "https://strong-hare.10web.cloud/wp-content/uploads/2026/06/ChatGPT-Image-Jun-4-2026-03_28_52-PM-1.png?v=kwgpreddf6",
  srcset: "https://strong-hare.10web.cloud/wp-content/uploads/2026/06/ChatGPT-Image-Jun-4-2026-03_28_52-PM-1.png?v=yr2khv5tzsl-wvc-srcset",
  sizes: "https://strong-hare.10web.cloud/wp-content/uploads/2026/06/ChatGPT-Image-Jun-4-2026-03_28_52-PM-1.png?v=jsoaw7wa5m-wvc-sizes",
  alt: "Professional medical collaboration and diagnostic review between healthcare providers"
}];
function ServiceBlock({
  service,
  index
}: {
  service: Service;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const isReversed = index % 2 === 1;
  return <div ref={ref} data-index={index} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-0 items-center">
        <div className={`lg:col-span-6 ${isReversed ? "lg:order-2" : "lg:order-1"}`}>
          <div className="aspect-[4/5] w-full overflow-hidden rounded-md shadow-md border border-border">
            <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
          </div>
        </div>

        <div className={`lg:col-span-6 ${isReversed ? "lg:order-1 lg:pr-20" : "lg:order-2 lg:pl-20"}`}>
          <div className="max-w-xl">
            <p className="font-mono text-xs tracking-widest uppercase mb-6 text-primary">
              {service.number}
            </p>
            <h3 className="font-serif font-default text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05] text-foreground mb-5">
              {service.title}
            </h3>
            <p className="font-default text-xl text-slate-text mb-8 leading-snug">
              {service.descriptor}
            </p>
            <p className="text-[17px] leading-relaxed mb-10 text-foreground/80">
              {service.body}
            </p>

            <div className="mb-10">
              <p className="font-mono text-xs tracking-widest uppercase text-slate-text mb-5">
                Clinical Focus
              </p>
              <ul className="space-y-3">
                {service.includes.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3 text-[15px] text-foreground/85">
                    <span className="mt-2 inline-block h-1 w-1 rounded-md shrink-0 bg-teal-muted" aria-hidden="true" />
                    <span className="font-default">{item}</span>
                  </li>)}
              </ul>
            </div>

            <p className="font-mono text-xs tracking-widest uppercase text-slate-text mb-8">
              {service.pricing}
            </p>

            <Button asChild size="lg" className="rounded-md px-7 h-12 text-sm tracking-wide bg-primary text-primary-foreground hover:bg-teal-muted transition-colors">
              <Link to="tel:+14126729240">
                Call to Schedule
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>;
}
export default function ConditionsWeTreat() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="794" id="conditions-we-treat" className="bg-background text-foreground pt-32 sm:pt-40 lg:pt-[200px] pb-24 lg:pb-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div ref={introRef} className={`text-center mb-24 lg:mb-32 transition-all duration-1000 ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="font-mono text-xs sm:text-sm tracking-[0.25em] uppercase mb-10 text-primary">
            Clinical Excellence / Adult Pulmonary Care
          </p>
          <h2 className="font-serif font-default text-foreground leading-[0.95] mb-10" style={{
          fontSize: "clamp(3.5rem, 8vw, 4.5rem)"
        }}>
            Expert Care,{" "}
            <span className="italic font-light text-foreground/85">breathe easier.</span>
          </h2>
          <p className="mx-auto text-slate-text text-lg leading-relaxed font-default" style={{
          maxWidth: "60ch"
        }}>
            Specialized medical pathways designed to address your respiratory and sleep health with clinical precision and compassionate guidance.
          </p>
        </div>

        <div className="space-y-24 lg:space-y-[120px]">
          {services.map((service, i) => <div key={i} data-index={i}>
              <ServiceBlock service={service} index={i} />
              {i < services.length - 1 && <div className="mt-24 lg:mt-[120px] flex items-center justify-center gap-6">
                  <div className="h-px w-24 sm:w-32 bg-border" />
                  <span className="inline-block h-1.5 w-1.5 rotate-45 bg-teal-muted" aria-hidden="true" />
                  <div className="h-px w-24 sm:w-32 bg-border" />
                </div>}
            </div>)}
        </div>
      </div>
    </section>;
}